import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Key, Shield, Webhook, Copy, ExternalLink, Check, AlertTriangle, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Settings = () => {
  const [config, setConfig] = useState({ configured: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiKey, setApiKey] = useState('');

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/sendgrid`;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/sendgrid`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter your SendGrid API key');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/settings/sendgrid`, { api_key: apiKey.trim() });
      toast.success('SendGrid API key saved and validated');
      setApiKey('');
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Configure your SendGrid email provider</p>
      </div>

      <Tabs defaultValue="sendgrid" className="space-y-6">
        <TabsList>
          <TabsTrigger value="sendgrid" className="flex items-center gap-2" data-testid="sendgrid-tab">
            <Key className="w-4 h-4" />
            SendGrid
          </TabsTrigger>
          <TabsTrigger value="webhooks" className="flex items-center gap-2" data-testid="webhooks-tab">
            <Webhook className="w-4 h-4" />
            Webhooks
          </TabsTrigger>
        </TabsList>

        {/* SendGrid Configuration */}
        <TabsContent value="sendgrid" className="space-y-6">
          {/* Status Card */}
          <Card className={config.configured ? 'border-emerald-500/50' : 'border-amber-500/50'}>
            <CardContent className="flex items-center justify-between pt-6">
              <div className="flex items-center gap-3">
                {config.configured ? (
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-500" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                  </div>
                )}
                <div>
                  <p className="font-medium" data-testid="sendgrid-status">
                    {config.configured ? 'SendGrid Configured' : 'SendGrid Not Configured'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {config.configured
                      ? config.source === 'system'
                        ? 'Using system API key'
                        : `Custom key · Last updated: ${new Date(config.updated_at).toLocaleDateString()}`
                      : 'Add your SendGrid API key to start sending emails'
                    }
                  </p>
                </div>
              </div>
              {config.configured && (
                <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">Active</Badge>
              )}
            </CardContent>
          </Card>

          {/* API Key Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                SendGrid API Key
              </CardTitle>
              <CardDescription>
                Your API key is encrypted before storage and never exposed. You can override the system key with your own.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="SG.xxxxx..."
                  data-testid="sendgrid-api-key-input"
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-sendgrid-btn"
              >
                {saving ? 'Validating & Saving...' : config.configured ? 'Update API Key' : 'Save API Key'}
              </Button>
            </CardContent>
          </Card>

          {/* How to get key */}
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="flex items-start gap-3 pt-6">
              <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">How to get a SendGrid API Key</p>
                <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
                  <li>Sign up or log in at <strong>sendgrid.com</strong></li>
                  <li>Go to Settings &rarr; API Keys</li>
                  <li>Click "Create API Key" and choose "Full Access"</li>
                  <li>Copy the key (starts with SG.) and paste it above</li>
                </ol>
                <a
                  href="https://app.sendgrid.com/settings/api_keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                >
                  Open SendGrid API Keys
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Webhooks */}
        <TabsContent value="webhooks" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">SendGrid Event Webhook Setup</CardTitle>
              <CardDescription>
                Configure SendGrid to send bounce & complaint notifications to your app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Webhook URL */}
              <div className="p-4 rounded-lg bg-muted/50">
                <Label className="text-muted-foreground">Your Webhook URL</Label>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 p-3 rounded bg-background border text-sm font-mono break-all" data-testid="webhook-url">
                    {webhookUrl}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(webhookUrl)}
                    data-testid="copy-webhook-url"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Step-by-step guide */}
              <div className="space-y-4">
                <h4 className="font-medium">Setup Instructions</h4>

                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center flex-shrink-0">1</div>
                    <div>
                      <p className="font-medium">Go to SendGrid Event Webhook Settings</p>
                      <p className="text-sm text-muted-foreground">
                        In your SendGrid dashboard, go to Settings &rarr; Mail Settings &rarr; Event Webhook.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center flex-shrink-0">2</div>
                    <div>
                      <p className="font-medium">Paste the Webhook URL</p>
                      <p className="text-sm text-muted-foreground">
                        Set the HTTP Post URL to the webhook URL shown above.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center flex-shrink-0">3</div>
                    <div>
                      <p className="font-medium">Select Events</p>
                      <p className="text-sm text-muted-foreground">
                        Enable: <strong>Bounced</strong>, <strong>Spam Report</strong>, <strong>Delivered</strong>, <strong>Opened</strong>, <strong>Clicked</strong>.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center flex-shrink-0">4</div>
                    <div>
                      <p className="font-medium">Enable & Save</p>
                      <p className="text-sm text-muted-foreground">
                        Toggle the webhook ON and click Save. SendGrid will start sending event data to your app.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="https://app.sendgrid.com/settings/mail_settings"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="sm">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open SendGrid Mail Settings
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* What gets tracked */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">What Gets Tracked</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 rounded-lg border">
                  <h5 className="font-medium text-amber-600 dark:text-amber-400">Bounces</h5>
                  <p className="text-sm text-muted-foreground mt-1">
                    Hard bounces automatically add the email to your suppression list to protect sender reputation.
                  </p>
                </div>
                <div className="p-4 rounded-lg border">
                  <h5 className="font-medium text-destructive">Spam Reports</h5>
                  <p className="text-sm text-muted-foreground mt-1">
                    When recipients mark your email as spam, they're automatically suppressed from future campaigns.
                  </p>
                </div>
                <div className="p-4 rounded-lg border">
                  <h5 className="font-medium text-emerald-600 dark:text-emerald-400">Delivered</h5>
                  <p className="text-sm text-muted-foreground mt-1">
                    Tracks when emails are successfully delivered to the recipient's mail server.
                  </p>
                </div>
                <div className="p-4 rounded-lg border">
                  <h5 className="font-medium text-blue-600 dark:text-blue-400">Opens & Clicks</h5>
                  <p className="text-sm text-muted-foreground mt-1">
                    Tracks when recipients open your email or click links (requires SendGrid tracking enabled).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
